// Shapes and labels for the vehicle compliance expiries screen.
//
// THE STATE VOCABULARY HAS SIX VALUES AND 'unknown' IS ONE OF THEM. It is not a
// leftover for rows that have not loaded, it is the commonest state in the fleet:
// on the day this was written every one of the 27 in-scope vehicles had no
// insurance date and no inspection date, and eight had no licence disc date
// either. Mapping unknown to the same grey as ok would report a compliant fleet
// that nobody has checked, so it gets its own tone and its own count.
import type { ChipTone } from '../components/interviews/Chip'

export const EXPIRY_KINDS = ['licence_disc', 'insurance', 'inspection'] as const
export type ExpiryKind = (typeof EXPIRY_KINDS)[number]

export const KIND_LABEL: Record<ExpiryKind, string> = {
  licence_disc: 'Licence disc',
  insurance: 'Insurance cover',
  inspection: 'Vehicle inspection',
}

export type ExpiryState = 'lapsed' | 'due_final' | 'due_soon' | 'due_lead' | 'unknown' | 'ok'

export type ExpiryRow = {
  vehicle_id: string
  plate: string | null
  vehicle_name: string | null
  owner_id: string | null
  owner_name: string | null
  lifecycle_status: string | null
  operational: boolean | null
  operational_state: string | null
  manager_name: string | null
  licence_disc_expires_on: string | null
  licence_disc_state: ExpiryState | null
  insurance_expires_on: string | null
  insurance_state: ExpiryState | null
  inspection_expires_on: string | null
  inspection_state: ExpiryState | null
  lapsed_count: number
  due_count: number
  unknown_count: number
  worst_rank: number
  soonest_days: number | null
}

export type ExpirySummary = {
  kind: ExpiryKind
  kind_label: string
  vehicles: number
  lapsed: number
  due_7: number
  due_30: number
  due_60: number
  not_on_file: number
  ok: number
  lapsed_and_driving: number
  unknown_and_driving: number
}

export type ExpiryFeed = {
  rows: ExpiryRow[]
  summary: ExpirySummary[]
  can_edit: boolean
}

/** The chip for one cell. 'unknown' is amber, never grey and never green. */
export function expiryChip(
  state: ExpiryState | null,
  expiresOn: string | null,
): { tone: ChipTone; label: string } {
  switch (state) {
    case 'lapsed':
      return { tone: 'red', label: `Expired ${formatShort(expiresOn)}` }
    case 'due_final':
      return { tone: 'red', label: `${formatShort(expiresOn)}` }
    case 'due_soon':
      return { tone: 'amber', label: `${formatShort(expiresOn)}` }
    case 'due_lead':
      return { tone: 'amber', label: `${formatShort(expiresOn)}` }
    case 'ok':
      return { tone: 'green', label: `${formatShort(expiresOn)}` }
    default:
      return { tone: 'amber', label: 'Not on file' }
  }
}

export function daysWord(days: number | null): string {
  if (days === null) return ''
  if (days < 0) return `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago`
  if (days === 0) return 'today'
  return `in ${days} day${days === 1 ? '' : 's'}`
}

/** dd Mmm yyyy, the same shape the task titles use, so the two read as one system. */
export function formatShort(iso: string | null): string {
  if (!iso) return 'Not on file'
  const d = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })
}

export const FILTERS = [
  { value: 'attention', label: 'Needs attention' },
  { value: 'lapsed', label: 'Expired' },
  { value: 'unknown', label: 'Not on file' },
  { value: 'all', label: 'All' },
] as const

export type ExpiryFilter = (typeof FILTERS)[number]['value']

export function rowMatchesFilter(r: ExpiryRow, f: ExpiryFilter): boolean {
  switch (f) {
    case 'lapsed':
      return r.lapsed_count > 0
    case 'unknown':
      return r.unknown_count > 0
    case 'attention':
      return r.lapsed_count > 0 || r.due_count > 0 || r.unknown_count > 0
    default:
      return true
  }
}

/**
 * The tone of the Expired and Due chips on a kind's summary card.
 *
 * BLIND IS NOT CLEAN. When every vehicle's date is missing, "Expired 0" and
 * "Due in 60 0" are arithmetically true and completely misleading. Green there
 * tells the operator the fleet is covered when nobody has checked, which on the
 * day this shipped was the state of insurance and of the inspection certificate
 * across all 27 cars. Pulled out of the component so the rule can be pinned by a
 * test: it is the exact rule a future tidy-up would delete for looking odd.
 */
export function summaryZeroTone(s: { vehicles: number; not_on_file: number }): ChipTone {
  return s.vehicles > 0 && s.not_on_file === s.vehicles ? 'neutral' : 'green'
}

export function isBlind(s: { vehicles: number; not_on_file: number }): boolean {
  return s.vehicles > 0 && s.not_on_file === s.vehicles
}
